/* eslint-disable no-nested-ternary */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import logger from '@adc/logger'
// @ts-ignore next
import analytics from '@adc/analytics'
import instrument from '@adc/instrument'
import classnames from 'classnames/bind'
import { match } from 'ts-pattern'
import * as graph from '@adc/sdk'
import type { Listing } from '@adc/sdk/types'
import Button from '../../elementals/button'
import Icon from '../../elementals/icon'
import Text from '../../elementals/text'
import useBreakpoints from '../../../hooks/useBreakpoints'
import { ListingInfoCardRibbon } from './listingInfoCardRibbon'
import Overlay from './overlay'
import styles from './index.scss'
import {
  VerifiedImage,
  BackgroundImage,
  SecondaryItem,
  MapItem,
  VideoItem,
  TABS,
} from './parts'
import ImageWatermark from './overlay/imageWatermark'
import { watermarkTypes } from '../../../constants/watermarkTypes'
import getStreetViewAvailability from '../../../utils/getStreetViewAvailability'
import { getTabName } from './utils'

const cx = classnames.bind(styles)

export const LISTING_INFORMATION_PHOTO_WIDTH = 955 // was 750

/**
 * AlternateItems are used to fill positions in showMultiple mode e.g. Map
 */
export enum AlternateItemType {
  MapItem,
  StreetViewItem,
  VideoItem,
  CustomItem,
}

// Internal to PhotoGallery. Not part of the public API — no external consumers.
type AlternateItem = {
  showAlways?: boolean // if false, it will only show if there's no photo available for it
  itemType?: AlternateItemType
  renderer?: () => JSX.Element // required if itemType is CustomItem
}

const getCommonTrackingValues = (listing: Listing, additionalData = {}) => {
  const { morpheusListingId, morpheusPropertyId } =
    analytics.standardTracking.listing(listing) as {
      morpheusListingId: string | null
      morpheusPropertyId: string | null
    }
  return {
    morpheusListingId,
    morpheusPropertyId,
    ...additionalData,
  }
}

const trackEvent = (event: string, listing: Listing, additionalData = {}) => {
  analytics.track(event, getCommonTrackingValues(listing, additionalData))
}

const defaultAlternateItems: AlternateItem[] = [
  {
    itemType: AlternateItemType.MapItem,
    showAlways: true,
  },
  {
    itemType: AlternateItemType.StreetViewItem,
    showAlways: true,
  },
  {
    itemType: AlternateItemType.CustomItem,
    showAlways: true,
    renderer: () => (
      <div
        style={{
          borderRadius: 6,
          display: 'flex',
          background: 'lightgrey',
          height: '100%',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Text color='gray' weight='medium'>
          Custom Item Goes Here (TODO)
        </Text>
      </div>
    ),
  },
]

interface PhotoGalleryProps {
  config: Record<string, unknown>
  className?: string
  cls?: Record<string, string>
  displayBanner: boolean
  shouldShowRibbon?: boolean
  children?: React.ReactNode
  featureFlags?: Record<string, boolean>
  listing: Listing
  fluid?: boolean
  showMultiple?: boolean
  alternativeItems?: AlternateItem[]
  supportLargeDesktop?: boolean
  watermarkType?: keyof typeof watermarkTypes
  defaultPhotoConfigKey?: string
  enableStreetView?: boolean
  videoUrlOverride?: string
}

const PhotoGallery = ({
  config,
  className,
  cls = {},
  displayBanner = false,
  shouldShowRibbon = false,
  children,
  featureFlags = {},
  listing,
  fluid = false,
  showMultiple = false,
  alternativeItems = defaultAlternateItems,
  supportLargeDesktop = true,
  watermarkType,
  defaultPhotoConfigKey,
  enableStreetView = false,
  videoUrlOverride,
}: PhotoGalleryProps) => {
  // Initialize images state with listing photos to prevent flash of default image
  const [images, setImages] = useState<string[]>(() => {
    if (!listing?.photos?.length) {
      return []
    }
    return listing.photos
      .map(photo => photo?.file_descriptor?.urls?.cdn)
      .filter((url): url is string => !!url)
  })

  // Pass 'desktop' as SSR default to ensure consistent rendering
  const breakpoint = useBreakpoints('desktop')

  // Use desktop as default during SSR to render full layout server-side
  const isMobile = breakpoint === 'mobile'
  const isMobileOrTablet = breakpoint === 'mobile' || breakpoint === 'tablet'

  useEffect(() => {
    if (!listing?.photos?.length) {
      setImages([])
    } else {
      setImages(
        listing?.photos
          ?.map(photo => photo?.file_descriptor?.urls?.cdn)
          .filter((url): url is string => !!url)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listing?.photos?.length,
    listing?.photos?.[0]?.file_descriptor?.urls?.cdn, // eslint-disable-line react-hooks/exhaustive-deps
    listing?.primary_property_id,
  ])

  const addressTuple: string[] = useMemo(
    () =>
      listing.formatted_address?.filter(
        (address): address is string => !!address
      ) || ['', ''],
    [listing.formatted_address]
  )
  const carouselRef = useRef<HTMLDivElement>(null)
  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null)
  const [streetViewAvailable, setStreetViewAvailable] = useState(false)
  // Calculate coordinates from listing
  const coords = useMemo(() => {
    const { lat, lon } =
      listing?.primary_property?.summary?.address?.coordinates || {}
    if (
      lat !== undefined &&
      lat !== null &&
      lon !== undefined &&
      lon !== null
    ) {
      return { lat, lng: lon }
    }
    return undefined
  }, [listing?.primary_property?.summary?.address?.coordinates])

  const toggleLock = (open = true) => {
    if (typeof window === 'undefined') return

    const method = open ? 'add' : 'remove'
    document.documentElement.classList[method](cx('body-noscroll'))
  }
  // Check Street View availability whenever coords change
  useEffect(() => {
    ;(async () => {
      if (coords && enableStreetView) {
        try {
          const available = await getStreetViewAvailability(coords)
          setStreetViewAvailable(available)
        } catch (error) {
          logger.info(
            'Unhandled error in StreetView availability check:',
            error
          )
          setStreetViewAvailable(false)
        }
      }
    })().catch(error => {
      logger.info('Unhandled error in StreetView availability check:', error)
    })
  }, [coords, enableStreetView])

  const videoUrlForThisProperty =
    videoUrlOverride ||
    (listing.primary_property_id &&
    typeof featureFlags['fe.resi.property-video-config'] === 'object'
      ? // @ts-ignore next
        (featureFlags['fe.resi.property-video-config']?.[
          listing.primary_property_id
        ] as string)
      : undefined)

  // Compute dynamic tab indices based on what tabs are actually present
  // This ensures indices match the actual panes array in the Overlay
  const dynamicTabs = useMemo(() => {
    let currentIndex = 0
    const tabs = {
      NONE: null,
      PHOTOS: currentIndex++, // Always 0
      VIDEO: videoUrlForThisProperty ? currentIndex++ : -1,
      MAP: currentIndex++,
      STREET: enableStreetView && streetViewAvailable ? currentIndex++ : -1,
    }
    return tabs
  }, [videoUrlForThisProperty, enableStreetView, streetViewAvailable])

  const setActiveTab = useCallback(
    (newTabIndex: (typeof TABS)[keyof typeof TABS]) => {
      // Track when gallery opens (transition from null to any tab)
      if (activeTabIndex === null && newTabIndex !== null) {
        trackEvent(analytics.eventNames.photoGalleryOpen, listing)
        instrument.increment('web.pdp.gallery', 1, ['action:open_gallery'])
      }

      // Track tab-specific button clicks and views
      if (newTabIndex !== null) {
        const tabName = getTabName(newTabIndex, dynamicTabs)
        trackEvent(analytics.eventNames.photoGalleryViewTab, listing, {
          tab: tabName,
        })
        instrument.increment('web.pdp.gallery', 1, [`action:view_${tabName}`])
      }

      setActiveTabIndex(newTabIndex)
      toggleLock(true)
    },
    [activeTabIndex, listing, dynamicTabs]
  )

  const onClose = useCallback(() => {
    setActiveTabIndex(null)
    toggleLock(false)
  }, [])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // prevent buttons from being clicked when pressing enter
      if (event.key === 'Enter') {
        event.preventDefault()
      }
      if (activeTabIndex === null) return
      if (event.key === 'Escape') {
        onClose()
      }
    },
    [activeTabIndex, onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  const statusLabelRole = useMemo(() => {
    return match(listing)
      .with({ is_coming_soon: true }, () => ({
        color: 'success',
        icon: 'house',
      }))
      .with(
        { listing_status_group: graph.enums.ListingStatusGroup.Active },
        () => ({ color: 'success', icon: 'gavel' })
      )
      .with(
        { listing_status_group: graph.enums.ListingStatusGroup.Pending },
        () => ({ color: 'warning', icon: 'house-circle-check' })
      )
      .with(
        { listing_status_group: graph.enums.ListingStatusGroup.Sold },
        () => ({ color: 'warning', icon: 'house-circle-check' })
      )
      .with(
        { listing_status_group: graph.enums.ListingStatusGroup.Inactive },
        () => ({
          color: 'warning',
          icon: 'house',
        })
      )
      .otherwise(() => ({ statusGroup: '', color: '', icon: '' }))
  }, [listing])

  // Track component mount with/without video
  useEffect(() => {
    trackEvent(analytics.eventNames.photoGalleryMounted, listing, {
      hasVideo: Boolean(videoUrlForThisProperty),
      hasPhotos: Boolean(images.length > 0),
    })
    instrument.increment('web.pdp.gallery', 1, [
      'action:mounted',
      'video:' + (videoUrlForThisProperty ? 'yes' : 'no'),
      'photos:' + (images.length > 0 ? 'yes' : 'no'),
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  const mapButton = useMemo(
    () => (
      <Button
        icon
        size='small'
        elmId='property_gallery_ctrl_map'
        onClick={() => setActiveTab(dynamicTabs.MAP)}
      >
        <Icon name='map-marked' iconStyle='light' size='large' /> Map
      </Button>
    ),
    [dynamicTabs.MAP, setActiveTab]
  )

  const wontDisplayMultiple =
    !showMultiple || (isMobileOrTablet && showMultiple)

  const renderButtons = useCallback(
    () => (
      <>
        <div className={cx('left-button-group')}>
          {images.length > 1 && (
            <Button
              icon
              size='small'
              elmId='property_gallery_ctrl_image'
              onClick={() => setActiveTab(dynamicTabs.PHOTOS)}
            >
              <Icon name='image' iconStyle='light' size='large' />{' '}
              {images.length} Photos
            </Button>
          )}
          {videoUrlForThisProperty && wontDisplayMultiple && (
            <Button
              icon
              size='small'
              elmId='property_gallery_ctrl_video'
              onClick={() => {
                setActiveTab(dynamicTabs.VIDEO)
              }}
            >
              <Icon name='video' iconStyle='light' size='large' /> Video
            </Button>
          )}
        </div>
        {coords && wontDisplayMultiple && (
          <div className={cx('right-button-group')}>
            {mapButton}
            {enableStreetView && streetViewAvailable && (
              <Button
                icon
                size='small'
                elmId='property_gallery_ctrl_street'
                onClick={() => setActiveTab(dynamicTabs.STREET)}
              >
                <Icon name='street-view' iconStyle='light' size='large' />{' '}
                Street
              </Button>
            )}
          </div>
        )}
      </>
    ),
    [
      coords,
      dynamicTabs.PHOTOS,
      dynamicTabs.STREET,
      dynamicTabs.VIDEO,
      enableStreetView,
      images.length,
      mapButton,
      setActiveTab,
      streetViewAvailable,
      videoUrlForThisProperty,
      wontDisplayMultiple,
    ]
  )

  /**
   * START: showMultiple features here...
   */

  const onSecondaryPhotoClick = useCallback(
    (index: number, enabled: boolean) => {
      if (!enabled) return
      setActiveTab(dynamicTabs.PHOTOS)
      setTimeout(() => {
        if (carouselRef.current) {
          // @ts-ignore next
          carouselRef.current.slickGoTo(index) // eslint-disable-line @typescript-eslint/no-unsafe-call
        }
      }, 10)
    },
    [dynamicTabs.PHOTOS, setActiveTab]
  )

  interface SecondaryPhotoProps {
    index: number
    keyOverride?: number
    url?: string
    featureFlags: Record<string, boolean>
    className?: string
  }

  /**
   * This holds an actual secondary photo (not for other items)
   */
  const SecondaryPhoto = useCallback(
    ({ index, url, featureFlags, className }: SecondaryPhotoProps) => (
      <BackgroundImage
        className={cx(
          'secondary-photo',
          `index-${index}`,
          { pointer: Boolean(url) },
          className
        )}
        data-elm-id={`secondary_photo_${index}`}
        onClick={() => onSecondaryPhotoClick(index, Boolean(url))}
        featureFlags={featureFlags}
        url={url}
        defaultPhotoConfigKey={defaultPhotoConfigKey}
      />
    ),
    [onSecondaryPhotoClick, defaultPhotoConfigKey]
  )

  const hasMap = Boolean(coords)

  const renderSecondaryPhotoOrPrimary = useCallback(
    (correspondingImageIndex: number) => {
      const hasEnoughImages = images.length > correspondingImageIndex
      if (hasEnoughImages)
        return (
          <SecondaryPhoto
            key={correspondingImageIndex}
            index={correspondingImageIndex}
            url={images[correspondingImageIndex]}
            featureFlags={featureFlags}
          />
        )
      // In case there is not enough photos for all chiclets, we need to display the default "Photo coming soon photo"
      return (
        <SecondaryPhoto
          index={1}
          key={correspondingImageIndex}
          featureFlags={featureFlags}
        />
      )
    },
    [SecondaryPhoto, featureFlags, images]
  )

  /**
   * Returns the element for the alternate item.
   * MapItem, StreetViewItem, and VideoItem are special cases the we handle here.
   * Otherwise, it's a custom item with a custom render function.
   */
  const getItemElement = useCallback(
    (item: AlternateItem, index: number) => {
      return match(item)
        .with(
          { itemType: AlternateItemType.MapItem },
          { itemType: AlternateItemType.StreetViewItem },
          item =>
            hasMap ? (
              <MapItem
                defaultView={
                  item.itemType === AlternateItemType.StreetViewItem
                    ? renderSecondaryPhotoOrPrimary(index)
                    : undefined
                }
                key={index}
                index={index}
                coords={coords}
                setActiveTab={setActiveTab}
                showStreetView={
                  item.itemType === AlternateItemType.StreetViewItem
                }
                mapTabIndex={dynamicTabs.MAP}
                streetTabIndex={dynamicTabs.STREET}
              />
            ) : (
              renderSecondaryPhotoOrPrimary(index)
            )
        )
        .with({ itemType: AlternateItemType.VideoItem }, () =>
          videoUrlForThisProperty ? (
            <VideoItem
              key={index}
              index={index}
              setActiveTab={setActiveTab}
              videoUrl={videoUrlForThisProperty}
              videoTabIndex={dynamicTabs.VIDEO}
            />
          ) : (
            renderSecondaryPhotoOrPrimary(index)
          )
        )
        .with({ itemType: AlternateItemType.CustomItem }, item => {
          if (!item.renderer) {
            throw new Error('Custom item must have a renderer')
          }
          return (
            <SecondaryItem key={index} index={index} renderer={item.renderer} />
          )
        })
        .otherwise(() => {
          throw new Error('Unknown item type')
        })
    },
    [
      coords,
      dynamicTabs.MAP,
      dynamicTabs.STREET,
      dynamicTabs.VIDEO,
      hasMap,
      renderSecondaryPhotoOrPrimary,
      setActiveTab,
      videoUrlForThisProperty,
    ]
  )

  /**
   * Returns an array of elements that are filled with altItems where needed or required
   *
   * For 2 positions, we fill #2 then #1, with the altItems
   *
   * |------------------|---------|
   * |                  |         |
   * |                  |    1    |
   * |                  |         |
   * |        0         |---------|
   * |                  |         |
   * |                  |    2    |
   * |                  |         |
   * |------------------|---------|
   *
   * For 4 positions, we fill #4, #3, #2, #1, with the altItems
   *
   * |------------------|---------|---------|
   * |                  |         |         |
   * |                  |    1    |    3    |
   * |                  |         |         |
   * |        0         |---------|---------|
   * |                  |         |         |
   * |                  |    2    |    4    |
   * |                  |         |         |
   * |------------------|---------|---------|
   *
   *  Could easily be extended to handle 6, 8 etc. by just changing the countNeeded type
   */
  const getItemElements = useCallback(
    (countNeeded: 2 | 4) => {
      let position = countNeeded // current position to fill, e.g. (4, 3, 2, 1) or (2, 1)
      const sourceAlternativeItems = [...alternativeItems]
      const newItemElements: JSX.Element[] = []

      let altItem = sourceAlternativeItems.shift() // pull off the top of queue
      while (altItem && newItemElements.length < countNeeded) {
        // if showAlways or if there are not enough images to fill that position, then fill with alt
        if (altItem.showAlways || images.length <= position) {
          // images are 1-based due to primary
          newItemElements.unshift(getItemElement(altItem, position))
        } else {
          // use photo
          newItemElements.unshift(renderSecondaryPhotoOrPrimary(position))
        }
        position--
        altItem = sourceAlternativeItems.shift()
      }

      // fill the rest with photos
      while (newItemElements.length < countNeeded) {
        // fill with photo that belongs there
        newItemElements.unshift(renderSecondaryPhotoOrPrimary(position))
        position--
      }
      return newItemElements
    },
    [
      alternativeItems,
      getItemElement,
      images.length,
      renderSecondaryPhotoOrPrimary,
    ]
  )

  const getSecondaryItems = useMemo(() => {
    const items = match<typeof breakpoint, JSX.Element[]>(breakpoint)
      .with('mobile', () => [])
      .with('tablet', () => [])
      .with('laptop', () => getItemElements(2))
      .otherwise(() => getItemElements(4))
    return items
  }, [breakpoint, getItemElements])

  /**
   * END: showMultiple-specific code
   */

  const PrimaryOverlay = ({
    watermarkType,
  }: {
    watermarkType?: keyof typeof watermarkTypes
  }) => (
    <>
      <>
        {renderButtons()}
        {listing.listing_status_label && displayBanner && (
          <div
            className={cx('status-label', statusLabelRole.color)}
            data-elm-id='property_gallery_status_label'
          >
            <Text color='white' weight='medium'>
              {listing.listing_status_label}
            </Text>
          </div>
        )}
        <ImageWatermark type={watermarkType} />
      </>
    </>
  )

  // Stable key based on listing and image to prevent unnecessary remounting
  const stableKey = `${listing.primary_property_id || 'no-id'}-${
    images[0] || 'no-image'
  }`

  const imageUrl = images[0]
  const hasImages = images.length > 0

  const handleImageClick = useCallback(() => {
    if (hasImages) {
      setActiveTab(dynamicTabs.PHOTOS)
    }
  }, [dynamicTabs.PHOTOS, hasImages, setActiveTab])

  // Add 'hydrated' class after mount to disable media query fallbacks
  const rootRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (rootRef.current) {
      rootRef.current.classList.add(cx('hydrated'))
    }
  }, [])

  return (
    <>
      {shouldShowRibbon && !isMobile && (
        <ListingInfoCardRibbon
          listing={listing}
          statusLabelRole={statusLabelRole}
        />
      )}

      <div
        ref={rootRef}
        className={cx('root', className, { fluid, multiple: showMultiple })}
        data-elm-id='property_gallery_section'
      >
        {!showMultiple || isMobile ? (
          <>
            <div className={cx('image-container', { pointer: hasImages })}>
              <VerifiedImage
                key={stableKey}
                className={cx(cls.image, 'image')}
                data-elm-id='property_gallery_image'
                alt={`${addressTuple[0]} ${addressTuple[1]}`}
                onClick={handleImageClick}
                url={imageUrl}
                featureFlags={featureFlags}
                defaultPhotoConfigKey={defaultPhotoConfigKey}
              />
            </div>
            <PrimaryOverlay watermarkType={watermarkType} />
          </>
        ) : (
          <div
            className={cx('multi-container', {
              'large-desktop-support': supportLargeDesktop,
            })}
          >
            <div className={cx('primary-photo', { pointer: hasImages })}>
              <VerifiedImage
                key={stableKey}
                className={cx(cls.image, 'image')}
                data-elm-id='property_gallery_image'
                alt={`${addressTuple[0]} ${addressTuple[1]}`}
                onClick={handleImageClick}
                url={imageUrl}
                featureFlags={featureFlags}
                defaultPhotoConfigKey={defaultPhotoConfigKey}
              />
              <PrimaryOverlay watermarkType={watermarkType} />
            </div>
            <div className={cx('secondary-photos')}>{getSecondaryItems}</div>
          </div>
        )}

        {activeTabIndex !== null && (
          <Overlay
            config={config}
            // @ts-ignore next
            coords={coords}
            images={images}
            addressTuple={addressTuple}
            activeTabIndex={activeTabIndex}
            onTabChange={setActiveTab}
            onSave={(): void => undefined}
            onClose={onClose}
            featureFlags={featureFlags}
            carouselRef={carouselRef}
            enableStreetView={enableStreetView}
            streetViewAvailable={streetViewAvailable}
            videoUrl={videoUrlForThisProperty}
          >
            {/* @ts-ignore next */}
            {children}
          </Overlay>
        )}
      </div>
    </>
  )
}

export default PhotoGallery
